// The `App` namespace: every handler referenced by inline `onclick`/`onchange`/
// `onsubmit`/`oninput` attributes in the screen templates lives here, wired to
// `window.App` once in main.ts. This mirrors the prototype's global-function inline
// handlers, just namespaced to avoid polluting `window`.

import {
  state,
  setTab,
  addExpense,
  updateExpense,
  deleteExpenseById,
  setFilter,
  setMapView,
  setReportMode,
  deletePhotoById,
  updateTripField as stateUpdateTripField,
  addCountry as stateAddCountry,
  removeCountry as stateRemoveCountry,
  addCategory as stateAddCategory,
  removeCategory as stateRemoveCategory,
  reassignAndDeleteCategory,
  categoryInUse,
  categoryName,
  addCurrency as stateAddCurrency,
  removeCurrency as stateRemoveCurrency,
  setBaseCurrency as stateSetBaseCurrency,
  resetAllData,
} from './state';
import type { Trip } from './types';
import { openSheet, closeSheet } from './lib/sheet';
import { toast } from './lib/toast';
import { handlePhotoCapture as capturePhoto } from './lib/photoCapture';
import { exportCSV as doExportCSV, exportXLSX as doExportXLSX } from './lib/export';
import { renderAddExpenseSheet, renderExpenseDetailSheet, renderDeleteExpenseSheet, refreshExpenseList } from './screens/expenses';
import { renderPhotoDetailSheet, mountPhotoDetailSheet } from './screens/mapPhotos';
import { renderReassignCategorySheet, renderResetConfirmSheet } from './screens/settings';

function val(id: string): string {
  return (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value ?? '';
}

export const App = {
  setTab,
  closeSheet,

  /* ---- quick add (dashboard) ---- */
  submitQuickExpense(ev: Event): boolean {
    ev.preventDefault();
    const amount = parseFloat(val('qeAmount'));
    if (!amount || amount <= 0) return false;
    const date = val('qeDate');
    const currency = val('qeCurrency');
    const categoryId = val('qeCategory');
    const explanation = val('qeNote');
    addExpense({ date, amount, currency, categoryId, explanation }).then(() => {
      (document.getElementById('qeAmount') as HTMLInputElement | null)?.focus();
    });
    return false;
  },

  /* ---- add-expense sheet ---- */
  openAddExpenseSheet(): void {
    openSheet(renderAddExpenseSheet());
  },
  saveNewExpense(ev: Event): boolean {
    ev.preventDefault();
    const amount = parseFloat(val('naAmount'));
    if (!amount || amount <= 0) return false;
    addExpense({
      date: val('naDate'),
      amount,
      currency: val('naCurrency'),
      categoryId: val('naCategory'),
      explanation: val('naNote'),
    }).then(closeSheet);
    return false;
  },

  /* ---- expense detail / edit / delete ---- */
  openExpenseDetail(id: string): void {
    openSheet(renderExpenseDetailSheet(id));
  },
  saveExpenseEdit(ev: Event, id: string): boolean {
    ev.preventDefault();
    const amount = parseFloat(val('edAmount'));
    if (!amount || amount <= 0) return false;
    updateExpense(id, {
      date: val('edDate'),
      amount,
      currency: val('edCurrency'),
      categoryId: val('edCategory'),
      explanation: val('edNote'),
    }).then(closeSheet);
    return false;
  },
  confirmDeleteExpense(id: string): void {
    openSheet(renderDeleteExpenseSheet(id));
  },
  doDeleteExpense(id: string): void {
    deleteExpenseById(id).then(closeSheet);
  },

  /* ---- expenses screen filters ---- */
  setSearchFilter(v: string): void {
    state.filters.search = v;
    refreshExpenseList();
  },
  setCategoryFilter(v: string): void {
    setFilter('category', v);
  },
  setCurrencyFilter(v: string): void {
    setFilter('currency', v);
  },

  /* ---- photos / map ---- */
  setMapView,
  handlePhotoCapture(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) void capturePhoto(file);
  },
  openPhotoDetail(id: string): void {
    openSheet(renderPhotoDetailSheet(id));
    mountPhotoDetailSheet();
  },
  deletePhoto(id: string): void {
    deletePhotoById(id).then(closeSheet);
  },

  /* ---- reports ---- */
  setReportMode,

  /* ---- settings: trip details ---- */
  updateTripField(key: keyof Trip, value: string): void {
    void stateUpdateTripField(key, value as never);
  },
  updateTripBudget(value: string): void {
    void stateUpdateTripField('totalBudget', parseFloat(value) || 0);
  },
  addCountry(): void {
    const el = document.getElementById('newCountry') as HTMLInputElement | null;
    if (!el || !el.value.trim()) return;
    stateAddCountry(el.value).then(() => {
      el.value = '';
    });
  },
  removeCountry: stateRemoveCountry,

  /* ---- settings: categories ---- */
  addCategory(): void {
    const el = document.getElementById('newCategory') as HTMLInputElement | null;
    if (!el || !el.value.trim()) return;
    stateAddCategory(el.value).then(() => {
      el.value = '';
    });
  },
  removeCategory(id: string): void {
    if (categoryInUse(id)) {
      openSheet(renderReassignCategorySheet(id, categoryName(id)));
      return;
    }
    void stateRemoveCategory(id);
  },
  reassignAndDelete(categoryId: string): void {
    const toId = val('reassignCat');
    reassignAndDeleteCategory(categoryId, toId).then(closeSheet);
  },

  /* ---- settings: currencies ---- */
  addCurrency(): void {
    const el = document.getElementById('newCurrency') as HTMLInputElement | null;
    if (!el) return;
    stateAddCurrency(el.value).then((ok) => {
      if (ok) el.value = '';
      else toast('Enter a valid 3-letter currency code');
    });
  },
  removeCurrency: stateRemoveCurrency,
  setBaseCurrency: stateSetBaseCurrency,

  /* ---- settings: export ---- */
  exportCSV(): void {
    doExportCSV();
    toast('CSV export ready');
  },
  exportXLSX(): void {
    toast('Preparing Excel file…');
    doExportXLSX().then(() => toast('Excel export ready'));
  },

  /* ---- settings: danger zone ---- */
  confirmReset(): void {
    openSheet(renderResetConfirmSheet());
  },
  doReset(): void {
    resetAllData().then(closeSheet);
  },
};

export type AppNamespace = typeof App;
